import axios from 'axios';
import api from './api';
import type { InventoryBalance, InventoryItem, PaginatedResponse } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/item`;

// ── Error extraction ──────────────────────────────────────────────────────────

const INVENTORY_ERROR_MESSAGES: Record<string, string> = {
  INVENTORY_ITEM_DUPLICATE: 'An active inventory item already exists for this wine at this location.',
  INVENTORY_ITEM_INACTIVE: 'This inventory item is inactive.',
  INVENTORY_ITEM_NOT_FOUND: 'Inventory item not found.',
  WINE_NOT_FOUND: 'Wine not found.',
  WINE_INACTIVE: 'This wine is inactive and cannot be tracked in inventory.',
  LOCATION_NOT_FOUND: 'Location not found or does not belong to this venue.',
  INVALID_REORDER_POINT: 'Reorder point must be less than or equal to par level.',
  // Deletion protection — blocked when transactional references exist
  INVENTORY_ITEM_HAS_REFERENCES: 'This item cannot be deleted because it is referenced by existing movements, receipts, stock counts, or wine list items. Deactivate it instead.',
  INVENTORY_ITEM_HAS_MOVEMENTS: 'This item cannot be deleted because it has inventory movements. Deactivate it instead.',
};

export function extractInventoryError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errorObj?.['code'];
    if (typeof code === 'string' && INVENTORY_ERROR_MESSAGES[code]) {
      return INVENTORY_ERROR_MESSAGES[code];
    }
    const message = errorObj?.['message'] ?? data?.['message'];
    if (typeof message === 'string' && message) return message;
    if (err.response?.status === 409) return INVENTORY_ERROR_MESSAGES['INVENTORY_ITEM_DUPLICATE'];
    if (err.response?.status === 403) return 'You do not have permission to perform this action.';
    if (err.response?.status === 404) return 'Inventory item not found.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WineListLink {
  _id: string;
  wineListId: string;
  wineListName: string;
  wineListStatus: string;
  visibility: 'LIVE' | 'HIDDEN';
  listPrice?: number;
  currency?: string;
  active: boolean;
}

/**
 * Derived stock status. Computed from balance.quantity + item.reorderPoint.
 * OUT  = quantity <= 0
 * LOW  = quantity > 0 AND reorderPoint set AND quantity <= reorderPoint
 * OK   = reorderPoint set AND quantity > reorderPoint
 * null = reorderPoint not set — no alert configured for this item
 */
export type StockStatus = 'OUT' | 'LOW' | 'OK';

/** Item as returned by GET /item (balance joined by backend). */
export type InventoryItemWithBalance = InventoryItem & {
  balance: InventoryBalance | null;
  /** Derived by backend once Phase 10 backend is deployed. Optional for backward compat. */
  stockStatus?: StockStatus | null;
};

export interface CreateItemPayload {
  wineId: string;
  locationId?: string;
  bottleSize?: number;
  parLevel?: number;
  reorderPoint?: number;
  source?: InventoryItem['source'];
}

export type UpdateItemPayload = Partial<
  Pick<InventoryItem, 'parLevel' | 'reorderPoint' | 'bottleSize' | 'active'>
>;

// ── Service ───────────────────────────────────────────────────────────────────

const inventoryItemService = {
  /**
   * Get all active inventory items for a venue.
   * Balance is joined per item by the backend — access via item.balance.
   * status param filters by derived stock status (LOW/OUT/OK) — requires Phase 10 backend.
   */
  getItems: async (
    venueId: string,
    page = 1,
    pageSize = 50,
    sort = 'wineDisplay.name',
    order: 'asc' | 'desc' = 'asc',
    searchQuery?: string,
    locationId?: string,
    status?: StockStatus,
  ): Promise<PaginatedResponse<InventoryItemWithBalance>> => {
    const params: Record<string, unknown> = { page, pageSize, sort, order };
    if (searchQuery?.trim()) params.q = searchQuery.trim();
    if (locationId) params.locationId = locationId;
    if (status) params.status = status;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /** Get a single inventory item (without joined balance). */
  getItem: async (venueId: string, itemId: string): Promise<InventoryItem> => {
    const response = await api.get(`${base(venueId)}/${itemId}`);
    return response.data;
  },

  /** Create a new inventory item. wineDisplay is populated server-side. */
  createItem: async (venueId: string, payload: CreateItemPayload): Promise<InventoryItem> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  /** Update mutable config fields only (parLevel, reorderPoint, bottleSize, active). */
  updateItem: async (
    venueId: string,
    itemId: string,
    payload: UpdateItemPayload,
  ): Promise<InventoryItem> => {
    const response = await api.put(`${base(venueId)}/${itemId}`, payload);
    return response.data;
  },

  /**
   * Delete an inventory item.
   * Server blocks deletion if the item has any transactional references:
   * InventoryMovement, InventoryBalance, GoodsReceiptLine, StockCountLine, or WineListItem.
   * Returns INVENTORY_ITEM_HAS_REFERENCES if blocked — show the error and prompt the user to
   * deactivate instead (set active: false via updateItem).
   */
  deleteItem: async (venueId: string, itemId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${itemId}`);
  },

  /** Get all wine list items linked to this inventory item across all wine lists. */
  getWineListLinks: async (venueId: string, itemId: string): Promise<WineListLink[]> => {
    const response = await api.get(`${base(venueId)}/${itemId}/wine-list-links`);
    return response.data;
  },
};

export default inventoryItemService;
